import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import { Search, Plus, Trash2, Loader2, CheckSquare, Square, Thermometer, UserCheck, Download, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

const Leads = () => {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [leadsData, setLeadsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalLeads, setTotalLeads] = useState(0);

  // ── Filter state ──────────────────────────────────────────────
  const [searchText, setSearchText]     = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [dateFrom, setDateFrom]         = useState('');
  const [dateTo, setDateTo]             = useState('');
  const searchDebounceRef = useRef(null);

  const hasActiveFilter = searchText || filterType || filterStatus || filterSource || dateFrom || dateTo;

  // ── Download State ──────────────────────────────────────────────
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [showBulkDownloadMenu, setShowBulkDownloadMenu] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // ── Bulk Update State ─────────────────────────────────────────
  const [selectedIds, setSelectedIds]           = useState(new Set());
  const [employees, setEmployees]               = useState([]);
  const [showBulkModal, setShowBulkModal]       = useState(false);
  const [bulkEmployee, setBulkEmployee]         = useState('');
  const [bulkLeadType, setBulkLeadType]         = useState('');
  const [bulkLeadSource, setBulkLeadSource]     = useState('');
  const [bulkUpdating, setBulkUpdating]         = useState(false);
  const [bulkMessage, setBulkMessage]           = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === leadsData.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(leadsData.map(l => l._id)));
    }
  };

  const openBulkModal = async () => {
    setBulkEmployee('');
    setBulkLeadType('');
    setBulkLeadSource('');
    setBulkMessage('');
    setShowBulkModal(true);
    if (employees.length === 0) {
      setLoadingEmployees(true);
      try {
        const res = await api('/users');
        if (res.ok) {
          const users = await res.json();
          setEmployees(users.filter(u => u.role !== 'super-admin'));
        }
      } catch (e) {
        console.error('Error fetching employees', e);
      } finally {
        setLoadingEmployees(false);
      }
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) return;
    setBulkUpdating(true);
    setBulkMessage('');
    try {
      const updates = {};
      if (bulkLeadType) updates.leadType = bulkLeadType;
      if (bulkLeadSource) updates.source = bulkLeadSource;
      if (bulkEmployee) {
        const emp = employees.find(e => e._id === bulkEmployee);
        if (emp?.role === 'telecaller') updates.telecallerId = bulkEmployee;
        else updates.executiveId = bulkEmployee;
      }
      const res = await api('/leads/bulk-update', {
        method: 'PATCH',
        body: { leadIds: Array.from(selectedIds), updates }
      });
      const data = await res.json();
      if (data.failedCount > 0) {
        setBulkMessage(`⚠️ Updated ${data.successCount} leads. ${data.failedCount} failed.`);
      } else {
        setBulkMessage(`✅ Successfully updated ${data.successCount} leads!`);
      }
      setSelectedIds(new Set());
      setTimeout(() => setShowBulkModal(false), 2000);
      fetchLeads({ search: searchText, leadType: filterType, status: filterStatus, source: filterSource, dateFrom, dateTo, pageNum: 1, append: false });
    } catch (e) {
      setBulkMessage('❌ Bulk update failed. Please try again.');
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`WARNING: Are you sure you want to delete ${selectedIds.size} selected leads? This cannot be undone.`)) return;

    setBulkMessage('Deleting...');
    try {
      const res = await api('/leads/bulk-delete', {
        method: 'POST',
        body: { leadIds: Array.from(selectedIds) }
      });
      if (res.ok) {
        setBulkMessage(`✅ Deleted ${selectedIds.size} leads successfully.`);
        setSelectedIds(new Set());
        setTimeout(() => setBulkMessage(''), 2000);
        fetchLeads({ search: searchText, leadType: filterType, status: filterStatus, source: filterSource, dateFrom, dateTo, pageNum: 1, append: false });
      } else {
        setBulkMessage('❌ Failed to delete leads.');
      }
    } catch (e) {
      setBulkMessage('❌ Error deleting leads.');
    }
  };

  // ── Dynamic options from Lead Settings ───────────────────────
  const [leadTypes, setLeadTypes]     = useState([]);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [leadSources, setLeadSources] = useState([]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [typesRes, statusesRes, sourcesRes] = await Promise.all([
          api('/lead-types'),
          api('/lead-statuses'),
          api('/lead-sources')
        ]);
        if (typesRes.ok)    setLeadTypes(await typesRes.json());
        if (statusesRes.ok) setLeadStatuses(await statusesRes.json());
        if (sourcesRes.ok)  setLeadSources(await sourcesRes.json());
      } catch (e) {
        console.error('Error fetching lead settings:', e);
      }
    };
    if (user) fetchSettings();
  }, [user]);

  // ── Fetch from backend with filter params ─────────────────────
  const fetchLeads = async ({ search = '', leadType = '', status = '', source = '', dateFrom = '', dateTo = '', pageNum = 1, append = false } = {}) => {
    if (!user) return;
    try {
      if (!append) setLoading(true);
      const params = new URLSearchParams();
      if (search)   params.append('search',   search);
      if (leadType) params.append('leadType',  leadType);
      if (status)   params.append('status',    status);
      if (source)   params.append('source',    source);
      if (dateFrom) params.append('dateFrom',  dateFrom);
      if (dateTo)   params.append('dateTo',    dateTo);
      params.append('page', pageNum);
      params.append('limit', 100);

      const query = params.toString() ? `?${params.toString()}` : '';
      const response = await api(`/leads${query}`);
      if (response.ok) {
        const data = await response.json();
        if (append) {
          setLeadsData(prev => [...prev, ...(data.leads || [])]);
        } else {
          setLeadsData(data.leads || []);
        }
        setTotalLeads(data.total || 0);
        setPage(data.page || 1);
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchLeads();
  }, [user, role]);

  // Re-fetch when dropdown filters change immediately
  useEffect(() => {
    fetchLeads({ search: searchText, leadType: filterType, status: filterStatus, source: filterSource, dateFrom, dateTo, pageNum: 1, append: false });
  }, [filterType, filterStatus, filterSource, dateFrom, dateTo]);

  // Re-fetch search with debounce (400ms) to avoid API call on every keystroke
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      fetchLeads({ search: val, leadType: filterType, status: filterStatus, source: filterSource, dateFrom, dateTo, pageNum: 1, append: false });
    }, 400);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete lead "${name}"?`)) {
      try {
        const response = await api(`/leads/${id}`, { method: 'DELETE' });
        if (response.ok) {
          setLeadsData(leadsData.filter(l => l._id !== id));
          alert("Lead deleted successfully.");
        }
      } catch (error) {
        console.error("Error deleting lead:", error);
      }
    }
  };



  const clearFilters = () => {
    setSearchText('');
    setFilterType('');
    setFilterStatus('');
    setFilterSource('');
    setDateFrom('');
    setDateTo('');
    fetchLeads({ search: '', leadType: '', status: '', source: '', dateFrom: '', dateTo: '', pageNum: 1, append: false });
  };

  const handleDownload = async (format = 'xlsx', selectedOnly = false) => {
    try {
      setDownloading(true);
      setShowDownloadMenu(false);
      setShowBulkDownloadMenu(false);

      const params = new URLSearchParams();
      if (searchText) params.append('search', searchText);
      if (filterType) params.append('leadType', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (filterSource) params.append('source', filterSource);
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      
      params.append('format', format);
      
      if (selectedOnly && selectedIds.size > 0) {
        params.append('ids', Array.from(selectedIds).join(','));
      }

      const token = localStorage.getItem('crm_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/leads/export?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      if (selectedOnly) {
         setBulkMessage(`✅ Successfully downloaded ${selectedIds.size} leads.`);
         setTimeout(() => setBulkMessage(''), 2000);
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download leads.');
    } finally {
      setDownloading(false);
    }
  };

  const selectStyle = {
    padding: '8px 12px',
    borderRadius: 'var(--border-radius-md)',
    border: '1px solid var(--color-border-primary)',
    fontSize: '13px',
    background: 'white',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    outline: 'none'
  };

  return (
    <MainLayout title="Leads">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        {/* ── Left: Search + Filters ── */}
        <div style={{ display: 'flex', gap: '10px', flex: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Search */}
          <div style={{ position: 'relative', flex: 1, maxWidth: '260px' }}>
            <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
            <input
              type="text"
              placeholder="Search leads by name, email, phone..."
              value={searchText}
              onChange={handleSearchChange}
              style={{
                width: '100%',
                padding: '8px 12px 8px 34px',
                borderRadius: 'var(--border-radius-md)',
                border: '1px solid var(--color-border-primary)',
                fontSize: '13px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Temperature filter — dynamic from Lead Settings */}
          <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selectStyle}>
            <option value="">🌡️ Temperature</option>
            {leadTypes.map(t => (
              <option key={t._id} value={t.name}>{t.name}</option>
            ))}
          </select>

          {/* Stage / Status filter — dynamic from Lead Settings */}
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="">📋 Stage</option>
            {leadStatuses.map(s => (
              <option key={s._id} value={s.name}>{s.name}</option>
            ))}
          </select>

          {/* Source filter — dynamic from Lead Settings */}
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)} style={selectStyle}>
            <option value="">🌍 Source</option>
            {leadSources.map(s => (
              <option key={s._id} value={s.name}>{s.name}</option>
            ))}
          </select>

          {/* Date Range filters */}
          <input 
            type="date" 
            value={dateFrom} 
            onChange={e => setDateFrom(e.target.value)} 
            style={selectStyle}
            title="From Date"
          />
          <input 
            type="date" 
            value={dateTo} 
            onChange={e => setDateTo(e.target.value)} 
            style={selectStyle}
            title="To Date"
          />

          {/* Clear button — only shown when a filter is active */}
          {hasActiveFilter && (
            <button
              onClick={clearFilters}
              style={{
                padding: '8px 12px',
                borderRadius: 'var(--border-radius-md)',
                border: '1px solid var(--color-border-primary)',
                background: 'white',
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                cursor: 'pointer'
              }}
            >
              ✕ Clear
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>

          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={downloading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                borderRadius: 'var(--border-radius-md)',
                border: '1px solid var(--color-border-primary)',
                background: 'white',
                color: 'var(--color-text-primary)',
                fontSize: '13px',
                fontWeight: '600',
                cursor: downloading ? 'not-allowed' : 'pointer'
              }}
            >
              {downloading && !showBulkDownloadMenu ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
              Export
              <ChevronDown size={14} />
            </button>
            
            {showDownloadMenu && (
              <div style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: 'white',
                border: '1px solid var(--color-border-primary)',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                zIndex: 100,
                minWidth: '150px',
                overflow: 'hidden'
              }}>
                <button onClick={() => handleDownload('xlsx', false)} style={{
                  width: '100%', padding: '10px 16px', textAlign: 'left', border: 'none', background: 'white', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--color-border-tertiary)'
                }}>
                  📊 Download as Excel
                </button>
                <button onClick={() => handleDownload('csv', false)} style={{
                  width: '100%', padding: '10px 16px', textAlign: 'left', border: 'none', background: 'white', cursor: 'pointer', fontSize: '13px'
                }}>
                  📄 Download as CSV
                </button>
              </div>
            )}
          </div>

          <button
            onClick={() => navigate('/add-lead')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              borderRadius: 'var(--border-radius-md)',
              border: 'none',
              background: 'var(--color-brand-primary)',
              color: 'white',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <Plus size={16} /> Add lead
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>Loading leads...</div>
      ) : leadsData.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
          {hasActiveFilter ? 'No leads match the selected filters.' : 'No leads found. Add your first lead!'}
        </div>
      ) : (
        <div style={{
          background: 'var(--color-background-primary)',
          border: '1px solid var(--color-border-tertiary)',
          borderRadius: 'var(--border-radius-lg)',
          overflow: 'hidden',
          marginBottom: '80px' // Space for floating bar
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--color-background-secondary)' }}>
                <th style={{ padding: '12px 16px', width: '40px', borderBottom: '1px solid var(--color-border-tertiary)' }}>
                  <input
                    type="checkbox"
                    checked={leadsData.length > 0 && selectedIds.size === leadsData.length}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                  />
                </th>
                {['Name', 'Stage', 'Assigned To', 'Added By', 'Date Added', 'Action'].map((header, idx) => (
                  <th key={idx} style={{
                    fontSize: '11px', color: 'var(--color-text-tertiary)',
                    textAlign: 'left', padding: '12px 16px', fontWeight: '600',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    borderBottom: '1px solid var(--color-border-tertiary)'
                  }}>
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leadsData.map((item, idx) => (
                <tr key={item._id} style={{ 
                  borderBottom: idx === leadsData.length - 1 ? 'none' : '1px solid var(--color-border-tertiary)',
                  background: selectedIds.has(item._id) ? 'rgba(99,102,241,0.05)' : 'transparent'
                }}>
                  <td style={{ padding: '12px 16px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(item._id)}
                      onChange={() => toggleSelect(item._id)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div
                      style={{ fontWeight: '600', cursor: 'pointer', color: 'var(--color-brand-primary)' }}
                      onClick={() => navigate(`/lead/${item._id}`)}
                    >
                      {item.name}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-tertiary)' }}>{item.propertyInterest}</div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <Badge type={item.status === 'New' ? 'Hot' : 'Cold'}>{item.status}</Badge>
                      {item.leadType && <Badge type={item.leadType}>{item.leadType}</Badge>}
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                      TC: {item.assignedTelecallerName || 'None'}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--color-text-secondary)' }}>
                      Exec: {item.assignedExecutiveName || 'None'}
                    </div>
                  </td>
                  <td style={{ padding: '14px 16px', fontSize: '13px' }}>{item.addedByName}</td>
                  <td style={{ padding: '14px 16px', fontSize: '12px' }}>
                    {new Date(item.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => navigate(`/lead/${item._id}`)}
                        style={{
                          padding: '4px 8px', borderRadius: '4px',
                          border: '1px solid var(--color-border-primary)',
                          background: 'white', fontSize: '12px', cursor: 'pointer'
                        }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(item._id, item.name)}
                        style={{
                          padding: '4px 8px', borderRadius: '4px',
                          border: '1px solid #fee2e2', background: '#fff',
                          color: '#ef4444', fontSize: '12px', cursor: 'pointer'
                        }}
                        title="Delete Lead"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Load More Button */}
          {leadsData.length < totalLeads && (
            <div style={{ padding: '16px', borderTop: '1px solid var(--color-border-tertiary)', textAlign: 'center', background: 'var(--color-background-secondary)' }}>
              <div style={{ fontSize: '13px', color: 'var(--color-text-tertiary)', marginBottom: '12px' }}>
                Showing {leadsData.length} of {totalLeads} leads
              </div>
              <button
                onClick={() => fetchLeads({ search: searchText, leadType: filterType, status: filterStatus, source: filterSource, dateFrom, dateTo, pageNum: page + 1, append: true })}
                style={{
                  padding: '8px 24px',
                  borderRadius: '6px',
                  border: '1px solid var(--color-border-primary)',
                  background: 'white',
                  color: 'var(--color-text-primary)',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Load Next 100 Leads ↓
              </button>
            </div>
          )}
        </div>
      )}

      {/* Floating Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div style={{
          position: 'fixed', bottom: '30px', left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--color-surface, #fff)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          padding: '14px 24px', borderRadius: '14px',
          display: 'flex', alignItems: 'center', gap: '20px',
          zIndex: 1000, border: '1px solid var(--color-border-tertiary)'
        }}>
          <span style={{ fontWeight: '600', fontSize: '15px' }}>
            {selectedIds.size} lead{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div style={{ width: '1px', height: '22px', background: 'var(--color-border-tertiary)' }} />
          
          {bulkMessage && !showBulkModal ? (
            <span style={{ color: bulkMessage.includes('❌') ? '#ef4444' : bulkMessage.includes('⚠️') ? '#f59e0b' : '#22c55e', fontWeight: '500' }}>
              {bulkMessage}
            </span>
          ) : (
            <>
              <button onClick={openBulkModal} style={{
                padding: '10px 22px', background: 'var(--color-brand-primary)',
                color: 'white', border: 'none', borderRadius: '8px',
                fontWeight: '600', cursor: 'pointer', fontSize: '14px'
              }}>
                Bulk Update
              </button>

              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowBulkDownloadMenu(!showBulkDownloadMenu)}
                  disabled={downloading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    padding: '10px 22px', background: '#f0fdf4',
                    color: '#166534', border: '1px solid #bbf7d0', borderRadius: '8px',
                    fontWeight: '600', cursor: downloading ? 'not-allowed' : 'pointer', fontSize: '14px'
                  }}
                >
                  {downloading && showBulkDownloadMenu ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
                  Export Selected
                  <ChevronDown size={14} />
                </button>
                {showBulkDownloadMenu && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, marginBottom: '8px',
                    background: 'white', border: '1px solid var(--color-border-primary)',
                    borderRadius: '8px', boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
                    zIndex: 100, minWidth: '160px', overflow: 'hidden'
                  }}>
                    <button onClick={() => handleDownload('xlsx', true)} style={{
                      width: '100%', padding: '10px 16px', textAlign: 'left', border: 'none', background: 'white', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--color-border-tertiary)'
                    }}>
                      📊 Download as Excel
                    </button>
                    <button onClick={() => handleDownload('csv', true)} style={{
                      width: '100%', padding: '10px 16px', textAlign: 'left', border: 'none', background: 'white', cursor: 'pointer', fontSize: '13px'
                    }}>
                      📄 Download as CSV
                    </button>
                  </div>
                )}
              </div>

              {(role === 'super-admin' || role === 'manager') && (
                <button onClick={handleBulkDelete} style={{
                  padding: '10px 22px', background: '#fee2e2',
                  color: '#ef4444', border: '1px solid #fca5a5', borderRadius: '8px',
                  fontWeight: '600', cursor: 'pointer', fontSize: '14px'
                }}>
                  Delete Selected
                </button>
              )}
            </>
          )}

          <button onClick={() => { setSelectedIds(new Set()); setBulkMessage(''); }}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)', fontSize: '18px', display: 'flex' }}>
            ✕
          </button>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
        }}>
          <div style={{
            background: 'var(--color-surface, #fff)', padding: '32px',
            borderRadius: '16px', width: '100%', maxWidth: '480px',
            boxShadow: '0 24px 48px rgba(0,0,0,0.3)', position: 'relative'
          }}>
            <button onClick={() => setShowBulkModal(false)} style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)'
            }}>
              ✕
            </button>
            
            <h2 style={{ margin: '0 0 24px 0', fontSize: '20px', color: 'var(--color-text-primary)' }}>
              Bulk Update {selectedIds.size} Leads
            </h2>

            {loadingEmployees ? (
              <div style={{ padding: '40px', textAlign: 'center' }}><Loader2 className="spin" size={32} /></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                
                {(role === 'super-admin' || role === 'manager') && (
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
                      <UserCheck size={16} /> Assign to Employee (Optional)
                    </label>
                    <select
                      value={bulkEmployee}
                      onChange={(e) => setBulkEmployee(e.target.value)}
                      style={{
                        width: '100%', padding: '12px', borderRadius: '8px',
                        border: '1px solid var(--color-border-tertiary)',
                        background: 'var(--color-background-secondary)',
                        color: 'var(--color-text-primary)'
                      }}
                    >
                      <option value="">No Change</option>
                      {employees.map(emp => (
                        <option key={emp._id} value={emp._id}>{emp.name} ({emp.role})</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
                    <Thermometer size={16} /> Lead Temperature (Optional)
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setBulkLeadType('')}
                      style={{
                        padding: '8px 16px', borderRadius: '20px', border: '1px solid var(--color-border-tertiary)',
                        background: bulkLeadType === '' ? 'var(--color-text-primary)' : 'transparent',
                        color: bulkLeadType === '' ? 'var(--color-surface)' : 'var(--color-text-secondary)',
                        cursor: 'pointer', fontWeight: '500'
                      }}
                    >
                      No Change
                    </button>
                    {leadTypes.map(type => (
                      <button
                        key={type._id}
                        type="button"
                        onClick={() => setBulkLeadType(type.name)}
                        style={{
                          padding: '8px 16px', borderRadius: '20px', 
                          border: `1px solid ${type.color || 'var(--color-border-tertiary)'}`,
                          background: bulkLeadType === type.name ? (type.color || 'var(--color-brand-primary)') : 'transparent',
                          color: bulkLeadType === type.name ? '#fff' : (type.color || 'var(--color-text-secondary)'),
                          cursor: 'pointer', fontWeight: '500'
                        }}
                      >
                        {type.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: 'var(--color-text-primary)' }}>
                    <Thermometer size={16} /> Lead Source (Optional)
                  </label>
                  <select
                    value={bulkLeadSource}
                    onChange={(e) => setBulkLeadSource(e.target.value)}
                    style={{
                      width: '100%', padding: '12px', borderRadius: '8px',
                      border: '1px solid var(--color-border-tertiary)',
                      background: 'var(--color-background-secondary)',
                      color: 'var(--color-text-primary)'
                    }}
                  >
                    <option value="">No Change</option>
                    {leadSources.map(source => (
                      <option key={source._id} value={source.name}>{source.name}</option>
                    ))}
                  </select>
                </div>

                {bulkMessage && showBulkModal && (
                   <div style={{ color: bulkMessage.includes('❌') ? '#ef4444' : bulkMessage.includes('⚠️') ? '#f59e0b' : '#22c55e', fontWeight: '500', textAlign: 'center' }}>
                     {bulkMessage}
                   </div>
                )}

                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button
                    onClick={handleBulkUpdate}
                    disabled={bulkUpdating || (!bulkEmployee && !bulkLeadType && !bulkLeadSource)}
                    style={{
                      flex: 1, padding: '12px', background: 'var(--color-brand-primary)', color: 'white',
                      border: 'none', borderRadius: '8px', fontWeight: '600',
                      cursor: (bulkUpdating || (!bulkEmployee && !bulkLeadType && !bulkLeadSource)) ? 'not-allowed' : 'pointer',
                      display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px',
                      opacity: (bulkUpdating || (!bulkEmployee && !bulkLeadType && !bulkLeadSource)) ? 0.7 : 1
                    }}
                  >
                    {bulkUpdating && <Loader2 className="spin" size={18} />}
                    Update {selectedIds.size} Leads
                  </button>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    disabled={bulkUpdating}
                    style={{
                      flex: 1, padding: '12px', background: 'transparent', color: 'var(--color-text-secondary)',
                      border: '1px solid var(--color-border-tertiary)', borderRadius: '8px', fontWeight: '600', 
                      cursor: bulkUpdating ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </MainLayout>
  );
};

export default Leads;
